/**
 * The shapes of twins' Geospatial values for a 2D twin map: polygons, lines
 * (with a wide invisible hit area, so a thin line is easy to hover and click)
 * and points. Put it inside a `TurboTwinMap`; `extractGeoObjects` turns
 * Geospatial rows into `geoObjects`.
 *
 * The shape under the cursor takes the hover colour (a Multi* part, and every
 * ring of a MultiPolygon part), and so does the property or part `highlight`
 * names, say a menu row under the pointer. What a tooltip says is the host's:
 * `renderTooltip` returns its content, and a shape has no tooltip without it.
 * With `interactive` false the shapes take no events, so a click reaches the
 * map, even on a line. The shapes draw in Leaflet's overlay pane, or in the
 * react-leaflet `Pane` around the layer.
 */
import { Fragment, useCallback, useMemo, useState, type ReactNode } from 'react';
import { CircleMarker, Polygon, Polyline, Tooltip } from 'react-leaflet';
import {
  GEO_SHAPE_COLORS,
  geoPathStyles,
  geoShapeEvents,
  geoShapeLit,
  smoothPath,
  type GeoHoverTarget,
  type GeoObject,
  type GeoShapeColors,
} from '../geoObjects';
import { isClosedPath } from '../geoValue';

export interface GeoShapesLayerProps {
  geoObjects: readonly GeoObject[];
  /** The owning twin is selected: thicker strokes in the selection colour. Default false. */
  selected?: boolean;
  /** Stroke and fill colour. */
  color: string;
  /** A property or part to light from outside the map (say, the menu row under the pointer). */
  highlight?: GeoHoverTarget | null;
  /** Selection and hover colours; each one left out keeps its default (GEO_SHAPE_COLORS). */
  colors?: Partial<GeoShapeColors>;
  /** Tooltip content for a shape of `geo`: sticky on polygons and lines. Shapes have no tooltip without it. */
  renderTooltip?: (geo: GeoObject) => ReactNode;
  /** A click on a shape: the property it draws and the part. */
  onGeoClick?: (propertyId: bigint, propertyName: string, part: number) => void;
  /** A right-click on a shape, at viewport (`x`, `y`): its property and part. */
  onGeoContextMenu?: (propertyName: string, part: number, x: number, y: number) => void;
  /**
   * The shapes take clicks and hover. False lets a click through to the map, even on a line, with
   * no hover colour and no tooltip; `highlight` still lights a shape. Default true.
   */
  interactive?: boolean;
}

/** The geospatial shapes as map layers: polygons, lines with their hit buffers, and points. */
export function GeoShapesLayer({
  geoObjects,
  selected = false,
  color,
  highlight,
  colors,
  renderTooltip,
  onGeoClick,
  onGeoContextMenu,
  interactive = true,
}: GeoShapesLayerProps) {
  // The shape under the cursor, this layer's own: a hover restyles this
  // layer's shapes whose state changed, nothing else on the page.
  const [cursor, setCursor] = useState<GeoHoverTarget | null>(null);
  const onHover = useCallback(
    (propertyName: string, part: number, on: boolean) =>
      setCursor(on ? { propertyName, part } : null),
    [],
  );
  // A change of `interactive` draws the shapes again, and a shape drawn again never hears the
  // mouse leave the one it replaced: forget the shape under the cursor.
  const [drawnInteractive, setDrawnInteractive] = useState(interactive);
  if (drawnInteractive !== interactive) {
    setDrawnInteractive(interactive);
    setCursor(null);
  }
  const tip = interactive ? renderTooltip : undefined;
  // Depends on the two colour strings, so an inline `colors` object restyles nothing.
  const styles = useMemo(() => {
    const palette: GeoShapeColors = {
      selected: colors?.selected ?? GEO_SHAPE_COLORS.selected,
      hover: colors?.hover ?? GEO_SHAPE_COLORS.hover,
    };
    return {
      plain: geoPathStyles(selected, color, false, palette),
      lit: geoPathStyles(selected, color, true, palette),
    };
  }, [selected, color, colors?.selected, colors?.hover]);
  // Lines and rings drawn smoothed, as in the 3D viewers; a closed line as a loop. Once per value,
  // not per hover.
  const drawn = useMemo(
    () =>
      geoObjects.map((geo) =>
        geo.geoType.endsWith('Point')
          ? geo.positions
          : geo.positions.map((ps) =>
              smoothPath(
                ps,
                geo.geoType.endsWith('Polygon') ||
                  isClosedPath(ps.map(([lat, lon]) => ({ lat, lon }))),
              ),
            ),
      ),
    [geoObjects],
  );
  const styleOf = (name: string, part: number) =>
    geoShapeLit([cursor, highlight ?? null], name, part) ? styles.lit : styles.plain;
  // Leaflet reads `interactive` only when it draws a shape (react-leaflet sets `pathOptions` after
  // that), so the key carries it: a change draws the shapes again.
  const keyOf = (geo: GeoObject, i: number) => `${geo.propertyId}-${i}-${interactive}`;

  return (
    <>
      {geoObjects.map((geo, g) => {
        const events = (i: number) =>
          geoShapeEvents(
            geo.name,
            geo.parts[i],
            onGeoClick && ((name, part) => onGeoClick(geo.propertyId, name, part)),
            onGeoContextMenu,
            onHover,
          );
        // Built once for all the shapes of this object.
        const tooltip = tip?.(geo);
        switch (geo.geoType) {
          case 'Polygon':
          case 'MultiPolygon':
            return drawn[g].map((ring, i) => (
              <Polygon
                key={keyOf(geo, i)}
                positions={ring}
                interactive={interactive}
                // Paths bubble to the map by default (markers don't); without
                // this a click on a shape reaches the map's own click handler
                // too (a host deselects there), right after this one.
                bubblingMouseEvents={false}
                pathOptions={styleOf(geo.name, geo.parts[i]).polygon}
                eventHandlers={events(i)}
              >
                {tip && <Tooltip sticky>{tooltip}</Tooltip>}
              </Polygon>
            ));
          case 'LineString':
          case 'MultiLineString':
            return drawn[g].map((line, i) => (
              <Fragment key={keyOf(geo, i)}>
                {/* Visible line — non-interactive so events fall through to the hit buffer */}
                <Polyline
                  positions={line}
                  interactive={false}
                  pathOptions={styleOf(geo.name, geo.parts[i]).line}
                />
                {/* Invisible hit buffer — wider hoverable / clickable area (~18px) */}
                {interactive && (
                  <Polyline
                    positions={line}
                    bubblingMouseEvents={false}
                    pathOptions={styles.plain.hit}
                    eventHandlers={events(i)}
                  >
                    {tip && <Tooltip sticky>{tooltip}</Tooltip>}
                  </Polyline>
                )}
              </Fragment>
            ));
          case 'Point':
          case 'MultiPoint':
            return geo.positions.flat().map((pt, i) => (
              <CircleMarker
                key={keyOf(geo, i)}
                center={pt}
                radius={selected ? 6 : 4}
                interactive={interactive}
                bubblingMouseEvents={false}
                pathOptions={styleOf(geo.name, geo.parts[i]).point}
                eventHandlers={events(i)}
              >
                {tip && <Tooltip>{tooltip}</Tooltip>}
              </CircleMarker>
            ));
          default:
            return null;
        }
      })}
    </>
  );
}
